import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Chip,
  Card,
  CardContent,
  InputAdornment,
  Menu,
  ListItemIcon,
  Autocomplete,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
  Avatar,
  Divider,
  CircularProgress,
  useTheme,
  Tabs,
  Tab,
  LinearProgress,
  Radio,
} from '@mui/material';
import {
  Download,
  Print,
  Refresh,
  Search as SearchIcon,
  Cancel as CancelIcon,
  CurrencyRupee as CurrencyRupeeIcon,
  CheckCircle as CheckCircleIcon,
  AccountBalance as AccountBalanceIcon,
  CheckCircle,
  Pending,
  Receipt,
  Phone,
  Email,
  Visibility,
  Close,
  Person,
  Home,
  MoneyOff,
  Business,
  Warning,
  PictureAsPdf,
  Close as CloseIcon,
  FilterAlt,
  DateRange,
  Today,
  CalendarMonth,
  FileDownload,
} from '@mui/icons-material';
import ExportButton from '../../components/ExportButton/ExportButton';
import RefreshButton from '../../components/RefreshButton/RefreshButton';
import FormatBadge from '../../components/FormatBadge/FormatBadge';
import { showSnackbar } from '../../components/Snackbar';
import { exportAsPDF, exportAsCSV, exportAsExcel } from '../../utils/importExport';
import AlertModal from '../../components/AlertModal';
import MoreFilters from '../../components/MoreFilters/MoreFilters';

import { getAssociates } from '../../services/associate';
import { getCancelledBookings } from '../../services/reports';

const CancelReport = () => {
  const theme = useTheme();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [filters, setFilters] = useState({
    fromDate: '',
    toDate: '',
    date: '',
    dateType: 'cancelledAt',
    cancelReason: '',
    associate: '',
    searchTerm: '',
  });
  const [sortBy, setSortBy] = useState('');
  const [moreFiltersExpanded, setMoreFiltersExpanded] = useState(false);
  const [selectedAssociate, setSelectedAssociate] = useState(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [cancellationChargePercent, setCancellationChargePercent] = useState('');
  const [viewTabValue, setViewTabValue] = useState(0);
  const [associates, setAssociates] = useState([]);
  const [associatesLoading, setAssociatesLoading] = useState(false);
  const [associateSearchInput, setAssociateSearchInput] = useState("");
  const [cancelledBookings, setCancelledBookings] = useState([]);
  const [totalBookings, setTotalBookings] = useState(0);
  const [bookingsLoading, setBookingsLoading] = useState(false);
  const associateSearchTimeoutRef = useRef(null);

  // Export Dialog States
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [exportType, setExportType] = useState('');
  const [exportDateMode, setExportDateMode] = useState('current');
  const [exportFromDate, setExportFromDate] = useState('');
  const [exportToDate, setExportToDate] = useState('');
  const [exportLoading, setExportLoading] = useState(false);
  const [alertModal, setAlertModal] = useState({ open: false, title: 'Alert', message: '' });
  const closeAlert = () => setAlertModal({ open: false, title: 'Alert', message: '' });

  // Sort mapping for cancelReport
  const sortMapping = {
    bookingDateDesc: { apiSortBy: 'bookingDate', apiSortOrder: 'desc' },
    bookingDateAsc:  { apiSortBy: 'bookingDate', apiSortOrder: 'asc' },
    cancelDateDesc:  { apiSortBy: 'cancelDate',  apiSortOrder: 'desc' },
    cancelDateAsc:   { apiSortBy: 'cancelDate',  apiSortOrder: 'asc' },
    propertyAmountDesc: { apiSortBy: 'propertyAmount', apiSortOrder: 'desc' },
    propertyAmountAsc:  { apiSortBy: 'propertyAmount', apiSortOrder: 'asc' },
    refundedAmountDesc: { apiSortBy: 'refundedAmount', apiSortOrder: 'desc' },
    refundedAmountAsc:  { apiSortBy: 'refundedAmount', apiSortOrder: 'asc' },
  };


  const fetchAssociates = async (searchKey = '') => {
    setAssociatesLoading(true);
    try {
      const params = { limit: 10 };
      if (searchKey && searchKey.trim().length > 0) {
        params.search = searchKey.trim();
        params.searchKey = searchKey.trim();
        params.filterKey = searchKey.trim();
      }
      const response = await getAssociates(params);
      let associatesData = [];
      if (response && response.data) {
        associatesData = Array.isArray(response.data) ? response.data : (response.data.docs || []);
      } else if (Array.isArray(response)) {
        associatesData = response;
      }
      const formattedAssociates = associatesData.map(associate => ({
        id: associate.id || associate._id || associate.associateId,
        name: associate.name || associate.fullName || `${associate.firstName || ''} ${associate.lastName || ''}`.trim(),
        email: associate.email || '',
        associateId: associate.associateId || associate.id || associate._id,
        ...associate
      }));
      setAssociates(formattedAssociates);
    } catch (error) {
      console.error('Failed to fetch associates:', error);
      setAssociates([]);
    } finally {
      setAssociatesLoading(false);
    }
  };

  useEffect(() => {
    fetchAssociates();
  }, []);

  useEffect(() => {
    fetchCancelledBookings({ page: 1, limit: 10 });
  }, []);

  const fetchCancelledBookings = async (searchParams = {}) => {
    setBookingsLoading(true);
    try {
      const activeSortBy = searchParams.sortBy !== undefined ? searchParams.sortBy : sortBy;
      const { apiSortBy, apiSortOrder } = (activeSortBy && sortMapping[activeSortBy]) || {};

      const activeDate     = searchParams.date     !== undefined ? searchParams.date     : filters.date;
      const activeDateType = searchParams.dateType !== undefined ? searchParams.dateType : filters.dateType;
      const activeFromDate = searchParams.fromDate !== undefined ? searchParams.fromDate : filters.fromDate;
      const activeToDate   = searchParams.toDate   !== undefined ? searchParams.toDate   : filters.toDate;

      const params = {
        fromDate: activeDate ? activeDate : activeFromDate,
        toDate:   activeDate ? activeDate : activeToDate,
        dateType: activeDateType || undefined,
        searchKey: searchParams.searchTerm !== undefined ? searchParams.searchTerm : filters.searchTerm,
        associateId: searchParams.associateId !== undefined ? searchParams.associateId : (selectedAssociate ? (selectedAssociate._id || selectedAssociate.id) : ''),
        sortBy: apiSortBy,
        sortOrder: apiSortOrder,
        page: searchParams.page || page + 1,
        limit: searchParams.limit || rowsPerPage,
      };
      Object.keys(params).forEach(key => {
        if (!params[key]) delete params[key];
      });

      const response = await getCancelledBookings(params);
      if (response.status && response.data) {
        const bookings = response.data.docs || [];
        const transformedBookings = bookings.map((booking, index) => {
          return {
            sno: ((response.data.page || 1) - 1) * (response.data.limit || 10) + index + 1,
            bookingId: `BK${booking.bookingId || booking._id?.slice(-4) || index + 1}`,
            bookingDate: booking.bookingDate ? new Date(booking.bookingDate).toISOString().split('T')[0] : '',
            cancelDate: booking.cancelledAt ? new Date(booking.cancelledAt).toISOString().split('T')[0] : (booking.approvedDate ? new Date(booking.approvedDate).toISOString().split('T')[0] : ''),
            customerName: booking.customer?.name || '',
            customerPhone: booking.customer?.phone || '',
            inventoryType: booking.particular?.inventoryType || '',
            plotNo: booking.particular?.inventoryDetail || '',
            plotAmount: booking.propertyAmount || 0,
            paidAmount: booking.totalReceived || 0,
            cancellationCharge: booking.cancellationChargeAmount ?? Math.round((booking.totalReceived || 0) * 0.05),
            refundAmount: booking.refundedAmount ?? Math.max(0, (booking.totalReceived || 0) - Math.round((booking.totalReceived || 0) * 0.05)),
            cancelReason: booking.cancellationReason || booking.remark || 'Not specified',
            refundMethod: booking.refundMethod || booking.mode || 'Not specified',
            status: 'Cancelled',
            associate: booking.associate?.name || '',
            cancelledBy: booking.cancelledBy || booking.updatedBy || 'System',
            associateId: booking.associate?.associateId || '',
            paymentReceipts: [],
            refunds: []
          };
        });

        setCancelledBookings(transformedBookings);
        setTotalBookings(response.data.totalDocs || 0);

        if (!hasSearched) {
          setHasSearched(true);
        }
      }
    } catch (error) {
      console.error('Failed to fetch cancelled bookings:', error);
      setCancelledBookings([]);
      setTotalBookings(0);
    } finally {
      setBookingsLoading(false);
    }
  };


  const customerSuggestions = useMemo(() => {
    const uniqueCustomers = [];
    const customerMap = new Map();

    (hasSearched ? cancelledBookings : []).forEach(booking => {
      if (!customerMap.has(booking.customerName)) {
        customerMap.set(booking.customerName, {
          name: booking.customerName,
          phone: booking.customerPhone || '',
          bookingId: booking.bookingId,
          plotNo: booking.plotNo,
          associate: booking.associate || ''
        });
      }
    });

    return Array.from(customerMap.values());
  }, [cancelledBookings, hasSearched]);

  const cancelReasons = [
    'Customer Request',
    'Payment Default',
    'Document Verification Failed',
    'Policy Violation',
    'Duplicate Booking',
    'Administrative Error',
    'Other',
  ];

  const handleSearch = async () => {
    setSearchLoading(true);
    setHasSearched(true);
    setPage(0);
    try {
      await fetchCancelledBookings({ page: 1 });
    } catch (error) {
      console.error('Error fetching cancelled bookings:', error);
    }
    setSearchLoading(false);
  };

  const handleClearFilters = () => {
    setFilters({
      fromDate: '',
      toDate: '',
      date: '',
      dateType: 'cancelledAt',
      cancelReason: '',
      associate: '',
      searchTerm: '',
    });
    setSortBy('');
    setSelectedAssociate(null);
    setAssociateSearchInput('');
    setHasSearched(true);
    setPage(0);
    setCancelledBookings([]);

    // Reload initial data
    fetchCancelledBookings({
      page: 1,
      limit: rowsPerPage,
      searchTerm: '',
      fromDate: '',
      toDate: '',
      date: '',
      dateType: 'cancelledAt',
      sortBy: '',
      associateId: ''
    });
  };

  const buildExportRows = () => {
    const exportData = hasSearched ? cancelledBookings : getFilteredData();
    return exportData.map((booking, i) => ({
      'S.No': i + 1,
      'Booking ID': booking.bookingId,
      'Booking Date': booking.bookingDate,
      'Cancel Date': booking.cancelDate,
      'Customer Name': booking.customerName,
      'Property Type': booking.inventoryType || '',
      'Property No': booking.plotNo,
      'Property Amount': booking.plotAmount || 0,
      'Paid Amount': booking.paidAmount || 0,
      'Refund Amount': booking.refundAmount || 0,
      'Cancel Reason': booking.cancelReason,
      'Associate': booking.associate,
      'Cancelled By': booking.cancelledBy,
    }));
  };

  const handleOpenExportDialog = (type) => {
    setExportType(type);
    setExportDialogOpen(true);
    setExportFromDate(filters.fromDate || '');
    setExportToDate(filters.toDate || '');
    setExportDateMode('current');
  };

  const handleCloseExportDialog = () => {
    setExportDialogOpen(false);
    setExportType('');
    setExportLoading(false);
  };

  const handleConfirmExport = async () => {
    setExportLoading(true);
    try {
      const params = {
        export: 'true',
      };

      if (selectedAssociate) {
        params.associateId = selectedAssociate._id || selectedAssociate.id;
      }
      if (filters.searchTerm) {
        params.searchKey = filters.searchTerm;
      }

      // Apply date filters only if NOT in "All Dates (Full History)" mode
      if (exportDateMode !== 'all') {
        if (exportDateMode === 'current') {
          if (filters.fromDate) params.fromDate = filters.fromDate;
          if (filters.toDate) params.toDate = filters.toDate;
        } else if (exportDateMode === 'custom') {
          if (exportFromDate) params.fromDate = exportFromDate;
          if (exportToDate) params.toDate = exportToDate;
        } else if (exportDateMode === 'single') {
          if (exportFromDate) {
            params.fromDate = exportFromDate;
            params.toDate = exportFromDate;
          }
        }
      }

      const response = await getCancelledBookings(params);
      if (response.status && response.data) {
        const bookings = response.data.docs || [];
        const transformedBookings = bookings.map((booking, index) => {
          return {
            sno: index + 1,
            bookingId: `BK${booking.bookingId || booking._id?.slice(-4) || index + 1}`,
            bookingDate: booking.bookingDate ? new Date(booking.bookingDate).toISOString().split('T')[0] : '',
            cancelDate: booking.cancelledAt ? new Date(booking.cancelledAt).toISOString().split('T')[0] : (booking.approvedDate ? new Date(booking.approvedDate).toISOString().split('T')[0] : ''),
            customerName: booking.customer?.name || '',
            customerPhone: booking.customer?.phone || '',
            inventoryType: booking.particular?.inventoryType || '',
            plotNo: booking.particular?.inventoryDetail || '',
            plotAmount: booking.propertyAmount || 0,
            paidAmount: booking.totalReceived || 0,
            cancellationCharge: booking.cancellationChargeAmount ?? Math.round((booking.totalReceived || 0) * 0.05),
            refundAmount: booking.refundedAmount ?? Math.max(0, (booking.totalReceived || 0) - Math.round((booking.totalReceived || 0) * 0.05)),
            cancelReason: booking.cancellationReason || booking.remark || 'Not specified',
            refundMethod: booking.refundMethod || booking.mode || 'Not specified',
            status: 'Cancelled',
            associate: booking.associate?.name || '',
            cancelledBy: booking.cancelledBy || booking.updatedBy || 'System',
          };
        });

        if (transformedBookings.length === 0) {
          setAlertModal({ open: true, title: 'Warning', message: 'No data found for the selected filter/date range.' });
          setExportLoading(false);
          return;
        }

        const rows = transformedBookings.map((booking, i) => ({
          'S.No': i + 1,
          'Booking ID': booking.bookingId,
          'Booking Date': booking.bookingDate,
          'Cancel Date': booking.cancelDate,
          'Customer Name': booking.customerName,
          'Property Type': booking.inventoryType || '',
          'Property No': booking.plotNo,
          'Property Amount': booking.plotAmount || 0,
          'Paid Amount': booking.paidAmount || 0,
          'Refund Amount': booking.refundAmount || 0,
          'Cancel Reason': booking.cancelReason,
          'Associate': booking.associate,
          'Cancelled By': booking.cancelledBy,
        }));

        const fileName = `cancel_report_${new Date().toISOString().slice(0, 10)}`;

        if (exportType === 'csv') {
          exportAsCSV({ rows, fileNameBase: fileName });
        } else if (exportType === 'pdf') {
          const formattedRows = rows.map(r => ({
            ...r,
            'Property Amount': `₹${r['Property Amount'].toLocaleString('en-IN')}`,
            'Paid Amount': `₹${r['Paid Amount'].toLocaleString('en-IN')}`,
            'Refund Amount': `₹${r['Refund Amount'].toLocaleString('en-IN')}`,
          }));

          exportAsPDF({
            rows: formattedRows,
            title: 'Cancel Booking Report',
            fileNameBase: fileName,
            orientation: 'l'
          });
        } else if (exportType === 'excel') {
          exportAsExcel({
            rows,
            sheetName: 'Cancel Bookings',
            fileNameBase: fileName
          });
        }

        handleCloseExportDialog();
      } else {
        setAlertModal({ open: true, title: 'Warning', message: 'No data found for the selected filter/date range.' });
      }
    } catch (error) {
      console.error('Export failed:', error);
      showSnackbar({ type: 'error', message: 'Export failed. Please try again.' });
    } finally {
      setExportLoading(false);
    }
  };

  const handleView = (booking) => {
    setSelectedBooking(booking);
    if (booking.cancellationCharge && booking.paidAmount) {
      const calculatedPercent = ((booking.cancellationCharge / booking.paidAmount) * 100).toFixed(2);
      setCancellationChargePercent(calculatedPercent);
    } else {
      setCancellationChargePercent('');
    }
    setViewDialogOpen(true);
  };

  const handleCloseViewDialog = () => {
    setViewDialogOpen(false);
    setSelectedBooking(null);
    setCancellationChargePercent('');
    setViewTabValue(0);
  };

  const getFilteredData = useCallback(() => {
    if (!hasSearched && cancelledBookings.length === 0) {
      return [];
    }

    let filteredData = [...cancelledBookings];

    // Apply customer search filter
    if (filters.searchTerm) {
      const searchLower = filters.searchTerm.toLowerCase();
      filteredData = filteredData.filter(item =>
        item.customerName.toLowerCase().includes(searchLower) ||
        item.customerPhone?.includes(filters.searchTerm) ||
        item.bookingId.toLowerCase().includes(searchLower) ||
        item.plotNo.toLowerCase().includes(searchLower) ||
        (item.associate && item.associate.toLowerCase().includes(searchLower))
      );
    }

    // Apply date filters (cancel date)
    if (filters.fromDate) {
      filteredData = filteredData.filter(item => item.cancelDate >= filters.fromDate);
    }
    if (filters.toDate) {
      filteredData = filteredData.filter(item => item.cancelDate <= filters.toDate);
    }

    // Apply cancel reason filter
    if (filters.cancelReason) {
      filteredData = filteredData.filter(item => item.cancelReason === filters.cancelReason);
    }

    // Apply associate filter
    if (filters.associate) {
      filteredData = filteredData.filter(item => item.associate === filters.associate);
    }

    return filteredData;
  }, [hasSearched, filters.searchTerm, filters.fromDate, filters.toDate, filters.cancelReason, filters.associate, cancelledBookings]);

  const filteredData = useMemo(() => getFilteredData(), [getFilteredData, cancelledBookings, hasSearched]);

  const totalCancelled = hasSearched ? totalBookings : filteredData.length;
  const totalRefundAmount = (hasSearched ? cancelledBookings : filteredData).reduce((sum, item) => sum + item.refundAmount, 0);
  const totalCancellationCharges = (hasSearched ? cancelledBookings : filteredData).reduce((sum, item) => sum + item.cancellationCharge, 0);
  const totalPaidAmount = (hasSearched ? cancelledBookings : filteredData).reduce((sum, item) => sum + item.paidAmount, 0);

  const paginatedData = hasSearched ? cancelledBookings : filteredData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <Box sx={{ p: 2, mr: 5, backgroundColor: '#f5f5f5', minHeight: '100vh' }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700, color: '#000', mb: 0.5 }}>
              Cancel Booking Report
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Track and manage cancelled bookings
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <ExportButton
              onExportPdf={() => handleOpenExportDialog('pdf')}
              onExportCsv={() => handleOpenExportDialog('csv')}
              onExportExcel={() => handleOpenExportDialog('excel')}
            />
            <RefreshButton onClick={() => fetchCancelledBookings()} loading={bookingsLoading} />
          </Box>
        </Box>
      </Box>

      {/* Summary Cards */}
      {/* <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ 
            borderRadius: 3, 
            p: 3, 
            textAlign: 'center', 
            bgcolor: '#fff',
            boxShadow: '0 4px 20px rgba(211, 47, 47, 0.15)',
            background: 'linear-gradient(135deg, #fff 0%, #fff5f5 100%)',
            border: '1px solid rgba(211, 47, 47, 0.1)',
            transition: 'all 0.3s ease-in-out',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: '0 8px 30px rgba(211, 47, 47, 0.25)',
            }
          }}>
            <Box sx={{ 
              display: 'inline-flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              p: 2,
              borderRadius: '50%',
              bgcolor: 'rgba(211, 47, 47, 0.1)',
              mb: 2
            }}>
              <CancelIcon sx={{ fontSize: 32, color: '#d32f2f' }} />
            </Box>
            <Typography variant="h4" sx={{ fontWeight: 700, color: '#1a1a1a', mb: 0.5 }}>
                {totalCancelled}
              </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600, fontSize: '0.875rem' }}>
              Total Cancelled
            </Typography>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ 
            borderRadius: 3, 
            p: 3, 
            textAlign: 'center', 
            bgcolor: '#fff',
            boxShadow: '0 4px 20px rgba(46, 125, 50, 0.15)',
            background: 'linear-gradient(135deg, #fff 0%, #f1f8f4 100%)',
            border: '1px solid rgba(46, 125, 50, 0.1)',
            transition: 'all 0.3s ease-in-out',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: '0 8px 30px rgba(46, 125, 50, 0.25)',
            }
          }}>
            <Box sx={{ 
              display: 'inline-flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              p: 2,
              borderRadius: '50%',
              bgcolor: 'rgba(46, 125, 50, 0.1)',
              mb: 2
            }}>
              <AccountBalanceIcon sx={{ fontSize: 32, color: '#2e7d32' }} />
            </Box>
            <Typography variant="h4" sx={{ fontWeight: 700, color: '#1a1a1a', mb: 0.5 }}>
                ₹{(totalRefundAmount / 100000).toFixed(2)}L
              </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600, fontSize: '0.875rem' }}>
              Total Refund Amount
            </Typography>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ 
            borderRadius: 3, 
            p: 3, 
            textAlign: 'center', 
            bgcolor: '#fff',
            boxShadow: '0 4px 20px rgba(211, 47, 47, 0.15)',
            background: 'linear-gradient(135deg, #fff 0%, #fff5f5 100%)',
            border: '1px solid rgba(211, 47, 47, 0.1)',
            transition: 'all 0.3s ease-in-out',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: '0 8px 30px rgba(211, 47, 47, 0.25)',
            }
          }}>
            <Box sx={{ 
              display: 'inline-flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              p: 2,
              borderRadius: '50%',
              bgcolor: 'rgba(211, 47, 47, 0.1)',
              mb: 2
            }}>
              <CurrencyRupeeIcon sx={{ fontSize: 32, color: '#d32f2f' }} />
            </Box>
            <Typography variant="h4" sx={{ fontWeight: 700, color: '#1a1a1a', mb: 0.5 }}>
                ₹{(totalCancellationCharges / 1000).toFixed(0)}K
              </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600, fontSize: '0.875rem' }}>
              Total Cancellation Charges
            </Typography>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ 
            borderRadius: 3, 
            p: 3, 
            textAlign: 'center', 
            bgcolor: '#fff',
            boxShadow: '0 4px 20px rgba(25, 118, 210, 0.15)',
            background: 'linear-gradient(135deg, #fff 0%, #f3f7ff 100%)',
            border: '1px solid rgba(25, 118, 210, 0.1)',
            transition: 'all 0.3s ease-in-out',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: '0 8px 30px rgba(25, 118, 210, 0.25)',
            }
          }}>
            <Box sx={{ 
              display: 'inline-flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              p: 2,
              borderRadius: '50%',
              bgcolor: 'rgba(25, 118, 210, 0.1)',
              mb: 2
            }}>
              <CheckCircleIcon sx={{ fontSize: 32, color: '#1976d2' }} />
            </Box>
            <Typography variant="h4" sx={{ fontWeight: 700, color: '#1a1a1a', mb: 0.5 }}>
                ₹{(totalPaidAmount / 100000).toFixed(2)}L
              </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600, fontSize: '0.875rem' }}>
              Total Paid Amount
            </Typography>
          </Card>
        </Grid>
      </Grid> */}

      {/* Filters */}
      <Card sx={{ mb: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderRadius: 2 }}>
        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
          <Grid container spacing={2} alignItems="center">
            {/* <Grid item xs={12} sm={6} md={2.4}>
              <FormControl fullWidth size="small">
                <InputLabel>Cancel Reason</InputLabel>
                <Select 
                  label="Cancel Reason" 
                  value={filters.cancelReason}
                  onChange={(e) => {
                    setFilters({ ...filters, cancelReason: e.target.value });
                  }}
                >
                  <MenuItem value="">All Reasons</MenuItem>
                  {cancelReasons.map((reason) => (
                    <MenuItem key={reason} value={reason}>{reason}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid> */}
            {/* Search / Associate / Buttons row */}
            <Grid item xs={12} sm={6} md={4}>
              <Autocomplete
                freeSolo
                size="small"
                options={customerSuggestions}
                getOptionLabel={(option) => {
                  if (typeof option === 'string') return option;
                  return option.name || '';
                }}
                onInputChange={(_, newValue) => {
                  setFilters({ ...filters, searchTerm: newValue });
                }}
                value={filters.searchTerm || null}
                onChange={(_, newValue) => {
                  if (typeof newValue === 'string') {
                    setFilters({ ...filters, searchTerm: newValue });
                  } else if (newValue) {
                    setFilters({ ...filters, searchTerm: newValue.name });
                  } else {
                    setFilters({ ...filters, searchTerm: '' });
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Search by Customer Name, Phone, Booking ID, or Property No"
                    placeholder="Type to search..."
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <>
                          <InputAdornment position="start">
                            <SearchIcon color="action" />
                          </InputAdornment>
                          {params.InputProps.startAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                renderOption={(props, option) => (
                  <Box component="li" {...props}>
                    <Box sx={{ width: '100%' }}>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {option.name}
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#666', display: 'block', mt: 0.25 }}>
                        {option.phone} • {option.bookingId} • {option.plotNo}
                      </Typography>
                    </Box>
                  </Box>
                )}
                filterOptions={(options, { inputValue }) => {
                  if (!inputValue) return options;
                  const searchLower = inputValue.toLowerCase();
                  return options.filter(option =>
                    option.name.toLowerCase().includes(searchLower) ||
                    option.phone.includes(inputValue) ||
                    option.bookingId.toLowerCase().includes(searchLower) ||
                    option.plotNo.toLowerCase().includes(searchLower) ||
                    (option.associate && option.associate.toLowerCase().includes(searchLower))
                  );
                }}
                noOptionsText="No customers found"
                clearOnEscape
                selectOnFocus
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Autocomplete
                size="small"
                options={associates}
                loading={associatesLoading}
                getOptionLabel={(opt) => {
                  if (!opt) return '';
                  if (typeof opt === 'string') return opt;
                  const name = opt.name || 'N/A';
                  const mobile = opt.phone || opt.mobile || opt.whatsapp || '';
                  return mobile ? `${name} — ${mobile}` : `${name}`;
                }}
                isOptionEqualToValue={(option, value) => {
                  if (!option || !value) return false;
                  return option.id === value.id;
                }}
                filterOptions={(options) => options}
                value={selectedAssociate}
                inputValue={associateSearchInput}
                onInputChange={(event, newInputValue, reason) => {
                  if (event && event.type === 'blur') return;
                  setAssociateSearchInput(newInputValue);
                  if (reason === 'reset') return;
                  if (associateSearchTimeoutRef.current) clearTimeout(associateSearchTimeoutRef.current);
                  associateSearchTimeoutRef.current = setTimeout(() => {
                    fetchAssociates(newInputValue);
                  }, 500);
                }}
                onChange={(_, newValue) => {
                  setSelectedAssociate(newValue);
                  setFilters({ ...filters, associate: newValue ? newValue.name : '' });
                  setAssociateSearchInput(newValue ? (newValue.phone ? `${newValue.name} — ${newValue.phone}` : newValue.name) : '');
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Associate"
                    placeholder="Search associate..."
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {associatesLoading ? <CircularProgress color="inherit" size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                renderOption={(props, option) => (
                  <Box component="li" {...props}>
                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>{option.name || 'N/A'}</Typography>
                      {(option.phone || option.mobile || option.whatsapp || option.email) && (
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                          {option.phone || option.mobile || option.whatsapp || option.email}
                          {option.associateId && ` • ID: ${option.associateId}`}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                )}
                noOptionsText={associatesLoading ? 'Loading associates...' : 'No associates found'}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant="contained"
                  onClick={handleSearch}
                  disabled={searchLoading}
                  startIcon={searchLoading ? <CircularProgress size={16} color="inherit" /> : <SearchIcon />}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 600,
                    backgroundColor: theme.palette.primary.main,
                    flex: 1,
                    '&:hover': { backgroundColor: theme.palette.primary.dark },
                    '&:disabled': { backgroundColor: '#e0e0e0', color: '#9e9e9e' },
                  }}
                >
                  {searchLoading ? '...' : 'Search'}
                </Button>
                <Button
                  variant="outlined"
                  onClick={handleClearFilters}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 600,
                    borderColor: theme.palette.primary.main,
                    color: theme.palette.primary.main,
                    '&:hover': { borderColor: theme.palette.primary.dark, backgroundColor: '#f0f7ff' },
                  }}
                >
                  Reset
                </Button>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <MoreFilters
                expanded={moreFiltersExpanded}
                onToggle={() => setMoreFiltersExpanded(!moreFiltersExpanded)}
                sortBy={sortBy}
                onChangeSortBy={setSortBy}
                type="cancelReport"
                height="40px"
                onApply={() => {
                  setPage(0);
                  fetchCancelledBookings({ page: 1 });
                }}
                onReset={() => {
                  setSortBy('');
                  const resetFilters = { ...filters, fromDate: '', toDate: '', date: '', dateType: 'cancelledAt' };
                  setFilters(resetFilters);
                  fetchCancelledBookings({
                    page: 1,
                    sortBy: '',
                    fromDate: '',
                    toDate: '',
                    date: '',
                    dateType: 'cancelledAt',
                  });
                }}
              >
                {/* Date Type */}
                <FormControl fullWidth size="small">
                  <InputLabel>Date Type</InputLabel>
                  <Select
                    label="Date Type"
                    value={filters.dateType}
                    onChange={(e) => setFilters({ ...filters, dateType: e.target.value })}
                  >
                    <MenuItem value="cancelledAt">Cancel Date</MenuItem>
                    <MenuItem value="bookingDate">Booking Date</MenuItem>
                  </Select>
                </FormControl>

                {/* Single Date */}
                <TextField
                  fullWidth
                  size="small"
                  type="date"
                  label="Single Date"
                  InputLabelProps={{ shrink: true }}
                  value={filters.date}
                  onChange={(e) => setFilters({ ...filters, date: e.target.value, fromDate: '', toDate: '' })}
                />

                {/* Date Range */}
                <Grid container spacing={1.5}>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      size="small"
                      type="date"
                      label="From Date"
                      InputLabelProps={{ shrink: true }}
                      value={filters.fromDate}
                      onChange={(e) => setFilters({ ...filters, fromDate: e.target.value, date: '' })}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      size="small"
                      type="date"
                      label="To Date"
                      InputLabelProps={{ shrink: true }}
                      value={filters.toDate}
                      onChange={(e) => setFilters({ ...filters, toDate: e.target.value, date: '' })}
                    />
                  </Grid>
                </Grid>
              </MoreFilters>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Results Table */}
      <Card sx={{ boxShadow: '0 2px 12px rgba(0,0,0,0.1)', borderRadius: 2, mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Cancelled Bookings
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
              {filteredData.length} booking{filteredData.length !== 1 ? 's' : ''} found
            </Typography>
          </Box>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: theme.palette.primary.main }}>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>S.No</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>Booking ID</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>Booking Date</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>Cancel Date</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>Customer Name</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>Property Type</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>Property No</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>Property Amount</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>Paid Amount</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>Refund Amount</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>Cancel Reason</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>Associate</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px', textAlign: 'center' }}>Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {bookingsLoading ? (
                  <TableRow>
                    <TableCell colSpan={13} align="center" sx={{ py: 4 }}>
                      <CircularProgress size={40} />
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        Loading cancelled bookings...
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : paginatedData.length > 0 ? (
                  paginatedData.map((booking) => (
                    <TableRow
                      key={booking.sno}
                      hover
                      sx={{
                        '&:nth-of-type(odd)': { backgroundColor: '#f9f9f9' },
                        '&:hover': { backgroundColor: '#f0f7ff !important' }
                      }}
                    >
                      <TableCell>{booking.sno}</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{booking.bookingId}</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{booking.bookingDate}</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: '#d32f2f' }}>{booking.cancelDate}</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{booking.customerName}</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{booking.inventoryType}</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{booking.plotNo}</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>₹{booking.plotAmount.toLocaleString('en-IN')}</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: '#2e7d32' }}>₹{booking.paidAmount.toLocaleString('en-IN')}</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: '#2e7d32' }}>₹{booking.refundAmount.toLocaleString('en-IN')}</TableCell>
                      <TableCell sx={{ maxWidth: 200 }}>
                        <Tooltip title={booking.cancelReason} arrow placement="top">
                          <Chip
                            label={booking.cancelReason}
                            size="small"
                            variant="outlined"
                            sx={{
                              fontWeight: 600,
                              maxWidth: '100%',
                              '& .MuiChip-label': {
                                display: 'block',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis'
                              }
                            }}
                          />
                        </Tooltip>
                      </TableCell>
                      <TableCell>{booking.associate}</TableCell>
                      <TableCell sx={{ textAlign: 'center' }}>
                        <Tooltip title="View Details">
                          <IconButton
                            size="small"
                            onClick={() => handleView(booking)}
                            sx={{ color: theme.palette.primary.main }}
                          >
                            <Visibility fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={13} align="center" sx={{ py: 4 }}>
                      <Typography variant="body1" color="text.secondary">
                        {hasSearched ? 'No cancelled bookings found' : 'Click Search to view cancelled bookings'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination */}
          {filteredData.length > 0 && (
            <TablePagination
              
              component="div"
              count={hasSearched ? totalBookings : filteredData.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={async (event, newPage) => {
                setPage(newPage);
                if (hasSearched) {
                  await fetchCancelledBookings({ page: newPage + 1 });
                }
              }}
              onRowsPerPageChange={async (event) => {
                const newRowsPerPage = parseInt(event.target.value, 10);
                setRowsPerPage(newRowsPerPage);
                setPage(0);
                if (hasSearched) {
                  await fetchCancelledBookings({ page: 1, limit: newRowsPerPage });
                }
              }}
            />
          )}
        </CardContent>
      </Card>

      {/* View Details Dialog */}
      <Dialog
        open={viewDialogOpen}
        onClose={handleCloseViewDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 8px 40px rgba(0,0,0,0.15)'
          }
        }}
      >
        <DialogTitle sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          backgroundColor: theme.palette.primary.main,
          color: 'white',
          px: 3,
          py: 2
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Visibility />
            <Typography variant="h6" fontWeight="bold">
              Cancelled Booking Details
            </Typography>
          </Box>
          <IconButton
            onClick={handleCloseViewDialog}
            sx={{ color: 'white' }}
          >
            <Close />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 3 }}>
          {selectedBooking && (
            <>
              {/* Header Section */}
              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                mb: 3,
                p: 2,
                backgroundColor: '#f8f9fa',
                borderRadius: 2,
                border: '1px solid #e9ecef'
              }}>
                <Avatar sx={{ bgcolor: theme.palette.primary.main, width: 48, height: 48 }}>
                  <Person />
                </Avatar>
                <Box>
                  <Typography variant="h6" fontWeight="bold">
                    {selectedBooking.customerName}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Booking ID: {selectedBooking.bookingId}
                  </Typography>
                  <Chip
                    label="Cancelled"
                    size="small"
                    color="error"
                    sx={{ mt: 0.5 }}
                  />
                </Box>
              </Box>

              {/* Tabs */}
              <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
                <Tabs
                  value={viewTabValue}
                  onChange={(e, newValue) => setViewTabValue(newValue)}
                  sx={{
                    '& .MuiTab-root': {
                      textTransform: 'none',
                      fontWeight: 600,
                      minHeight: 48
                    }
                  }}
                >
                  <Tab label="Booking Details" icon={<Home />} iconPosition="start" />
                  <Tab
                    icon={<CurrencyRupeeIcon />}
                    iconPosition="start"
                    label="Refund History"
                  />
                </Tabs>
              </Box>

              {/* Tab Content */}
              {viewTabValue === 0 && (
                <Box>
                  {/* Booking Information */}
                  <Paper sx={{ p: 3, borderRadius: 2, mb: 3 }}>
                    <Typography variant="h6" fontWeight="bold" color="primary" gutterBottom>
                      Booking Information
                    </Typography>
                    <Divider sx={{ mb: 3 }} />
                    <Grid container spacing={3}>
                      <Grid item xs={12} md={6}>
                        <TextField fullWidth label="Booking ID" value={selectedBooking.bookingId || ''} disabled />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField fullWidth label="Booking Date" type="date" value={selectedBooking.bookingDate || ''} disabled InputLabelProps={{ shrink: true }} />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField fullWidth label="Property Number" value={selectedBooking.plotNo || ''} disabled />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField fullWidth label="Branch" value={selectedBooking.branchName || 'Not Available'} disabled />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField fullWidth label="Payment Plan" value={selectedBooking.paymentPlan || 'Not Available'} disabled />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField fullWidth label="Status" value={selectedBooking.status || 'Cancelled'} disabled />
                      </Grid>
                    </Grid>
                  </Paper>

                  {/* Customer Information */}
                  <Paper sx={{ p: 3, borderRadius: 2, mb: 3 }}>
                    <Typography variant="h6" fontWeight="bold" color="primary" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Person color="primary" />
                      Customer Information
                    </Typography>
                    <Divider sx={{ mb: 3 }} />
                    <Grid container spacing={3}>
                      <Grid item xs={12} md={6}>
                        <TextField fullWidth label="Customer Name" value={selectedBooking.customerName || ''} disabled />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label="Phone Number"
                          value={selectedBooking.customerPhone || ''}
                          disabled
                          InputProps={{
                            startAdornment: <InputAdornment position="start"><Phone fontSize="small" /></InputAdornment>,
                          }}
                        />
                      </Grid>
                    </Grid>
                  </Paper>

                  {/* Associate Information */}
                  <Paper sx={{ p: 3, borderRadius: 2, mb: 3 }}>
                    <Typography variant="h6" fontWeight="bold" color="primary" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Business color="primary" />
                      Associate Information
                    </Typography>
                    <Divider sx={{ mb: 3 }} />
                    <Grid container spacing={3}>
                      <Grid item xs={12} md={6}>
                        <TextField fullWidth label="Associate/Agent" value={selectedBooking.associate || 'Not Available'} disabled />
                      </Grid>
                    </Grid>
                  </Paper>

                  {/* Financial Summary */}
                  <Paper sx={{ p: 3, borderRadius: 2, mb: 3 }}>
                    <Typography variant="h6" fontWeight="bold" color="primary" gutterBottom>
                      Financial Summary
                    </Typography>
                    <Divider sx={{ mb: 3 }} />
                    <Grid container spacing={3}>
                      <Grid item xs={12} sm={6} md={3}>
                        <Box sx={{ textAlign: 'center', p: 2, bgcolor: '#f5f5f5', borderRadius: 2 }}>
                          <Typography variant="h5" fontWeight="bold" color="text.primary">
                            ₹{(selectedBooking.plotAmount || 0).toLocaleString('en-IN')}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>Total Property Amount</Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <Box sx={{ textAlign: 'center', p: 2, bgcolor: '#e8f5e9', borderRadius: 2 }}>
                          <Typography variant="h5" fontWeight="bold" color="success.main">
                            ₹{(selectedBooking.paidAmount || 0).toLocaleString('en-IN')}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>Paid Amount</Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <Box sx={{ textAlign: 'center', p: 2, bgcolor: '#fff3e0', borderRadius: 2 }}>
                          <Typography variant="h5" fontWeight="bold" color="warning.main">
                            ₹{((selectedBooking.plotAmount || 0) - (selectedBooking.paidAmount || 0)).toLocaleString('en-IN')}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>Remaining Amount</Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <Box sx={{ textAlign: 'center', p: 2, bgcolor: '#ffebee', borderRadius: 2 }}>
                          <Typography variant="h5" fontWeight="bold" color="error.main">
                            ₹{(selectedBooking.cancellationCharge || 0).toLocaleString('en-IN')}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>Cancellation Charge</Typography>
                        </Box>
                      </Grid>
                    </Grid>

                    {/* Payment Progress */}
                    {(selectedBooking.plotAmount || 0) > 0 && (
                      <Box sx={{ mt: 3 }}>
                        <Typography variant="subtitle2" fontWeight="600" sx={{ mb: 1 }}>
                          Payment Progress
                        </Typography>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                          <Typography variant="body2" fontWeight="600">
                            {(((selectedBooking.paidAmount || 0) / (selectedBooking.plotAmount || 1)) * 100).toFixed(1)}% Completed
                          </Typography>
                          <Typography variant="body2" fontWeight="600">
                            ₹{(selectedBooking.paidAmount || 0).toLocaleString('en-IN')} / ₹{(selectedBooking.plotAmount || 0).toLocaleString('en-IN')}
                          </Typography>
                        </Box>
                        <LinearProgress
                          variant="determinate"
                          value={((selectedBooking.paidAmount || 0) / (selectedBooking.plotAmount || 1)) * 100}
                          color="primary"
                          sx={{ height: 10, borderRadius: 5 }}
                        />
                      </Box>
                    )}
                  </Paper>

                  {/* Cancellation Details */}
                  <Paper sx={{ p: 3, borderRadius: 2, mb: 3, bgcolor: '#ffebee', border: '1px solid #f44336' }}>
                    <Typography variant="h6" fontWeight="bold" color="error" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Warning color="error" />
                      Cancellation Details
                    </Typography>
                    <Divider sx={{ mb: 3, borderColor: '#f44336' }} />
                    <Grid container spacing={3}>
                      <Grid item xs={12} sm={6} md={4}>
                        <Box>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>Cancel Date</Typography>
                          <Typography variant="body1" fontWeight="600" color="error.main">
                            {selectedBooking.cancelDate || '-'}
                          </Typography>
                        </Box>
                      </Grid>
                      {selectedBooking.cancelReason && (
                        <Grid item xs={12} sm={6} md={4}>
                          <Box>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>Cancel Reason</Typography>
                            <Chip
                              label={selectedBooking.cancelReason}
                              size="small"
                              color="error"
                              variant="outlined"
                              sx={{ fontWeight: 600 }}
                            />
                          </Box>
                        </Grid>
                      )}
                      <Grid item xs={12} sm={6} md={4}>
                        <Box>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>Cancelled By</Typography>
                          <Typography variant="body1" fontWeight="600">
                            {selectedBooking.cancelledBy || 'Not Specified'}
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>
                  </Paper>

                </Box>
              )}

              {/* Tab 1: Refund Receipt */}
              {viewTabValue === 1 && (
                <Box>
                  <Typography variant="h6" fontWeight="bold" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Receipt color="primary" />
                    Refund History
                  </Typography>
                  <Divider sx={{ mb: 3 }} />

                  {/* Refund Information */}
                  <Paper sx={{ p: 3, borderRadius: 2, bgcolor: '#e8f5e9', border: '1px solid #4caf50', mb: 3 }}>
                    <Typography variant="h6" fontWeight="bold" color="success.main" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CurrencyRupeeIcon color="success" />
                      Refund Information
                    </Typography>
                    <Divider sx={{ mb: 3, borderColor: '#4caf50' }} />
                    <Grid container spacing={3}>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label="Cancellation Charge (%)"
                          type="number"
                          disabled
                          value={cancellationChargePercent}
                          InputProps={{
                            endAdornment: <InputAdornment position="end">%</InputAdornment>,
                          }}
                          helperText={selectedBooking.cancellationCharge ? `Amount: ₹${selectedBooking.cancellationCharge.toLocaleString('en-IN')}` : ''}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label="Refund Amount"
                          value={selectedBooking.refundAmount || 0}
                          disabled
                          InputProps={{
                            startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                          }}
                          sx={{
                            '& .MuiInputBase-input': {
                              color: 'success.main',
                              fontWeight: 600,
                              fontSize: '1.1rem'
                            }
                          }}
                        />
                      </Grid>
                      {selectedBooking.refundMethod && (
                        <Grid item xs={12} md={6}>
                          <TextField
                            fullWidth
                            label="Refund Method"
                            value={selectedBooking.refundMethod}
                            disabled
                          />
                        </Grid>
                      )}
                    </Grid>
                  </Paper>

                  {/* Refund Summary */}
                  {(selectedBooking?.refunds && selectedBooking.refunds.length > 0) && (
                    <Grid container spacing={3} sx={{ mb: 3 }}>
                      <Grid item xs={12} md={3}>
                        <Card sx={{ p: 2, bgcolor: '#fff3e0', border: '1px solid #ffcc80' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                            <Typography variant="body2" color="text.secondary" fontWeight="600">
                              Total Refund
                            </Typography>
                            <MoneyOff color="error" />
                          </Box>
                          <Typography variant="h5" fontWeight="bold" color="error.main">
                            ₹{selectedBooking.refunds.reduce((sum, r) => sum + (r.amount || 0), 0).toLocaleString('en-IN')}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {selectedBooking.refunds.length} record(s)
                          </Typography>
                        </Card>
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <Card sx={{ p: 2, bgcolor: '#e8f5e9', border: '1px solid #81c784' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                            <Typography variant="body2" color="text.secondary" fontWeight="600">
                              Processed
                            </Typography>
                            <CheckCircle color="success" />
                          </Box>
                          <Typography variant="h5" fontWeight="bold" color="success.main">
                            ₹{selectedBooking.refunds
                              .filter(r => r.status === 'Processed')
                              .reduce((sum, r) => sum + (r.amount || 0), 0)
                              .toLocaleString('en-IN')}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {selectedBooking.refunds.filter(r => r.status === 'Processed').length} receipt(s)
                          </Typography>
                        </Card>
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <Card sx={{ p: 2, bgcolor: '#fff9c4', border: '1px solid #ffd54f' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                            <Typography variant="body2" color="text.secondary" fontWeight="600">
                              Pending
                            </Typography>
                            <Pending color="warning" />
                          </Box>
                          <Typography variant="h5" fontWeight="bold" color="warning.main">
                            ₹{selectedBooking.refunds
                              .filter(r => r.status === 'Pending')
                              .reduce((sum, r) => sum + (r.amount || 0), 0)
                              .toLocaleString('en-IN')}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {selectedBooking.refunds.filter(r => r.status === 'Pending').length} receipt(s)
                          </Typography>
                        </Card>
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <Card sx={{ p: 2, bgcolor: '#f5f5f5', border: '1px solid #bdbdbd' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                            <Typography variant="body2" color="text.secondary" fontWeight="600">
                              Total Paid
                            </Typography>
                            <CurrencyRupeeIcon color="action" />
                          </Box>
                          <Typography variant="h5" fontWeight="bold" color="text.primary">
                            ₹{(selectedBooking.paidAmount || 0).toLocaleString('en-IN')}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Paid Amount
                          </Typography>
                        </Card>
                      </Grid>
                    </Grid>
                  )}



                  {/* Refund Receipt Table */}
                  {/* <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ bgcolor: '#fafafa' }}>
                          <TableCell><strong>Receipt No</strong></TableCell>
                          <TableCell><strong>Date</strong></TableCell>
                          <TableCell><strong>Amount</strong></TableCell>
                          <TableCell><strong>Payment Mode</strong></TableCell>
                          <TableCell><strong>Status</strong></TableCell>
                          <TableCell><strong>Remarks</strong></TableCell>
                          <TableCell><strong>Actions</strong></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {selectedBooking?.refunds && selectedBooking.refunds.length > 0 ? (
                          selectedBooking.refunds.map((refund, refundIndex) => (
                            <TableRow key={refundIndex} sx={{ bgcolor: '#fff3e0' }}>
                              <TableCell>
                                <Typography variant="body2" fontWeight="600">
                                  {refund.receiptNo}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2">
                                  {refund.refundDate || '-'}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" fontWeight="600" color="error.main">
                                  ₹{refund.amount.toLocaleString('en-IN')}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2">
                                  {refund.method || '-'}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Chip
                                  label={refund.status}
                                  size="small"
                                  color={
                                    refund.status === 'Processed' ? 'success' :
                                      refund.status === 'Pending' ? 'warning' :
                                        'error'
                                  }
                                  sx={{ fontWeight: 600 }}
                                />
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" color="text.secondary">
                                  {refund.remarks || '-'}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                  <Tooltip title="Print Refund Record">
                                    <IconButton
                                      size="small"
                                      onClick={() => {
                                        const printWindow = window.open('', '_blank');
                                        if (!printWindow) return;
                                        const style = `
                                        <style>
                                          body { font-family: Arial, sans-serif; padding: 20px; }
                                          h1 { color: #d32f2f; margin-bottom: 20px; }
                                          table { width: 100%; border-collapse: collapse; margin-top: 20px; }
                                          th, td { border: 1px solid #ddd; padding: 10px; text-align: left; }
                                          th { background: #f5f5f5; font-weight: bold; }
                                          .header { background: #fff3e0; padding: 15px; border-radius: 5px; }
                                        </style>
                                      `;
                                        printWindow.document.write(`
                                        <html>
                                          <head>
                                            <title>Refund Record - ${refund.receiptNo}</title>
                                            ${style}
                                          </head>
                                          <body>
                                            <div class="header">
                                              <h1>REFUND RECORD</h1>
                                              <p><strong>Receipt No:</strong> ${refund.receiptNo}</p>
                                              <p><strong>Booking ID:</strong> ${selectedBooking.bookingId}</p>
                                              <p><strong>Customer Name:</strong> ${selectedBooking.customerName}</p>
                                              <p><strong>Property Number:</strong> ${selectedBooking.plotNo}</p>
                                              <p><strong>Refund Date:</strong> ${refund.refundDate || '-'}</p>
                                              <p><strong>Amount:</strong> ₹${refund.amount.toLocaleString('en-IN')}</p>
                                              <p><strong>Payment Mode:</strong> ${refund.method || '-'}</p>
                                              <p><strong>Status:</strong> ${refund.status}</p>
                                              ${refund.remarks ? `<p><strong>Remarks:</strong> ${refund.remarks}</p>` : ''}
                                            </div>
                                            <p style="margin-top: 30px; color: #666;">This is a system generated refund record.</p>
                                          </body>
                                        </html>
                                      `);
                                        printWindow.document.close();
                                        printWindow.focus();
                                        printWindow.print();
                                      }}
                                    >
                                      <Print fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title="Download Refund PDF">
                                    <IconButton
                                      size="small"
                                      onClick={() => {
                                        const printWindow = window.open('', '_blank');
                                        if (!printWindow) return;
                                        const style = `
                                        <style>
                                          body { font-family: Arial, sans-serif; padding: 20px; }
                                          h1 { color: #d32f2f; margin-bottom: 20px; }
                                          table { width: 100%; border-collapse: collapse; margin-top: 20px; }
                                          th, td { border: 1px solid #ddd; padding: 10px; text-align: left; }
                                          th { background: #f5f5f5; font-weight: bold; }
                                          .header { background: #fff3e0; padding: 15px; border-radius: 5px; }
                                        </style>
                                      `;
                                        printWindow.document.write(`
                                        <html>
                                          <head>
                                            <title>Refund Record - ${refund.receiptNo}</title>
                                            ${style}
                                          </head>
                                          <body>
                                            <div class="header">
                                              <h1>REFUND RECORD</h1>
                                              <p><strong>Receipt No:</strong> ${refund.receiptNo}</p>
                                              <p><strong>Booking ID:</strong> ${selectedBooking.bookingId}</p>
                                              <p><strong>Customer Name:</strong> ${selectedBooking.customerName}</p>
                                              <p><strong>Property Number:</strong> ${selectedBooking.plotNo}</p>
                                              <p><strong>Refund Date:</strong> ${refund.refundDate || '-'}</p>
                                              <p><strong>Amount:</strong> ₹${refund.amount.toLocaleString('en-IN')}</p>
                                              <p><strong>Payment Mode:</strong> ${refund.method || '-'}</p>
                                              <p><strong>Status:</strong> ${refund.status}</p>
                                              ${refund.remarks ? `<p><strong>Remarks:</strong> ${refund.remarks}</p>` : ''}
                                            </div>
                                            <p style="margin-top: 30px; color: #666;">This is a system generated refund record.</p>
                                          </body>
                                        </html>
                                      `);
                                        printWindow.document.close();
                                        printWindow.focus();
                                        printWindow.print();
                                      }}
                                    >
                                      <PictureAsPdf fontSize="small" color="error" />
                                    </IconButton>
                                  </Tooltip>
                                </Box>
                              </TableCell>
                            </TableRow>
                          ))
                        ) : (
                          <TableRow>
                            <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                              <Typography variant="body2" color="text.secondary">
                                No refund records available.
                              </Typography>
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer> */}
                </Box>
              )}
            </>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 3, backgroundColor: '#f8f9fa' }}>
          <Button
            onClick={handleCloseViewDialog}
            variant="contained"
            sx={{
              backgroundColor: theme.palette.primary.main,
              '&:hover': { backgroundColor: theme.palette.primary.dark }
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Export Date Range Dialog */}
      <Dialog
        open={exportDialogOpen}
        onClose={handleCloseExportDialog}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: 'hidden',
            boxShadow: '0 16px 48px rgba(83, 109, 254, 0.15), 0 2px 12px rgba(0,0,0,0.06)',
          }
        }}
      >
        {/* Gradient Header */}
        <Box sx={{
          background: 'linear-gradient(135deg, #536DFE 0%, #3d5afe 50%, #304ffe 100%)',
          px: 2.5, py: 1.8,
          position: 'relative', overflow: 'hidden',
        }}>
          <Box sx={{ position: 'absolute', top: -15, right: -15, width: 55, height: 55, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.08)' }} />
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'relative', zIndex: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
              <Box sx={{
                width: 34, height: 34, borderRadius: 2,
                bgcolor: 'rgba(255,255,255,0.18)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <FileDownload sx={{ color: '#fff', fontSize: 18 }} />
              </Box>
              <Box>
                <Typography sx={{ fontWeight: 700, color: '#fff', fontSize: '0.95rem', lineHeight: 1.2 }}>
                  Export Cancel Report
                </Typography>
                <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.68rem', mt: 0.2 }}>
                  Download your report data
                </Typography>
              </Box>
            </Box>
            <IconButton onClick={handleCloseExportDialog} size="small" sx={{ color: 'rgba(255,255,255,0.8)', p: 0.5, '&:hover': { bgcolor: 'rgba(255,255,255,0.15)' } }}>
              <CloseIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Box>
        </Box>

        <DialogContent sx={{ px: 2.5, pt: 2, pb: 1.5 }}>
          {/* Format Badge */}
          <FormatBadge type={exportType} />

          {/* Section label */}
          <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: '#555', mb: 1, textTransform: 'uppercase', letterSpacing: 0.6 }}>
            Select Date Range
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.8 }}>
            {/* Option 1: Current Filters */}
            <Box
              onClick={() => setExportDateMode('current')}
              sx={{
                display: 'flex', alignItems: 'center', gap: 1.5,
                px: 1.5, py: 1.2, borderRadius: 2, cursor: 'pointer',
                border: exportDateMode === 'current' ? '1.5px solid #536DFE' : '1.5px solid #e8eaf6',
                bgcolor: exportDateMode === 'current' ? 'rgba(83, 109, 254, 0.04)' : '#fff',
                transition: 'all 0.2s ease',
                '&:hover': { borderColor: '#536DFE', bgcolor: 'rgba(83, 109, 254, 0.02)' },
              }}
            >
              <Box sx={{
                width: 32, height: 32, borderRadius: 1.5,
                bgcolor: exportDateMode === 'current' ? '#536DFE' : '#e8eaf6',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                transition: 'all 0.2s ease', flexShrink: 0,
              }}>
                <FilterAlt sx={{ fontSize: 16, color: exportDateMode === 'current' ? '#fff' : '#536DFE' }} />
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontWeight: 600, fontSize: '0.8rem', color: '#1a1a2e' }}>
                  Current Filters
                </Typography>
                <Typography sx={{ fontSize: '0.68rem', color: '#999', mt: 0.1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {filters.fromDate && filters.toDate
                    ? `${filters.fromDate} → ${filters.toDate}`
                    : filters.fromDate ? `From ${filters.fromDate}` : filters.toDate ? `Till ${filters.toDate}` : 'All dates'}
                </Typography>
              </Box>
              <Radio checked={exportDateMode === 'current'} size="small" sx={{ p: 0.3, color: '#c5cae9', '&.Mui-checked': { color: '#536DFE' } }} />
            </Box>

            {/* Option 2: Custom Date Range */}
            <Box
              onClick={() => setExportDateMode('custom')}
              sx={{
                display: 'flex', alignItems: 'center', gap: 1.5,
                px: 1.5, py: 1.2, borderRadius: 2, cursor: 'pointer',
                border: exportDateMode === 'custom' ? '1.5px solid #536DFE' : '1.5px solid #e8eaf6',
                bgcolor: exportDateMode === 'custom' ? 'rgba(83, 109, 254, 0.04)' : '#fff',
                transition: 'all 0.2s ease',
                '&:hover': { borderColor: '#536DFE', bgcolor: 'rgba(83, 109, 254, 0.02)' },
              }}
            >
              <Box sx={{
                width: 32, height: 32, borderRadius: 1.5,
                bgcolor: exportDateMode === 'custom' ? '#536DFE' : '#e8eaf6',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                transition: 'all 0.2s ease', flexShrink: 0,
              }}>
                <DateRange sx={{ fontSize: 16, color: exportDateMode === 'custom' ? '#fff' : '#536DFE' }} />
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontWeight: 600, fontSize: '0.8rem', color: '#1a1a2e' }}>
                  Custom Date Range
                </Typography>
                <Typography sx={{ fontSize: '0.68rem', color: '#999', mt: 0.1 }}>
                  Choose from & to dates
                </Typography>
              </Box>
              <Radio checked={exportDateMode === 'custom'} size="small" sx={{ p: 0.3, color: '#c5cae9', '&.Mui-checked': { color: '#536DFE' } }} />
            </Box>

            {/* Option 3: Single Day */}
            <Box
              onClick={() => setExportDateMode('single')}
              sx={{
                display: 'flex', alignItems: 'center', gap: 1.5,
                px: 1.5, py: 1.2, borderRadius: 2, cursor: 'pointer',
                border: exportDateMode === 'single' ? '1.5px solid #536DFE' : '1.5px solid #e8eaf6',
                bgcolor: exportDateMode === 'single' ? 'rgba(83, 109, 254, 0.04)' : '#fff',
                transition: 'all 0.2s ease',
                '&:hover': { borderColor: '#536DFE', bgcolor: 'rgba(83, 109, 254, 0.02)' },
              }}
            >
              <Box sx={{
                width: 32, height: 32, borderRadius: 1.5,
                bgcolor: exportDateMode === 'single' ? '#536DFE' : '#e8eaf6',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                transition: 'all 0.2s ease', flexShrink: 0,
              }}>
                <Today sx={{ fontSize: 16, color: exportDateMode === 'single' ? '#fff' : '#536DFE' }} />
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontWeight: 600, fontSize: '0.8rem', color: '#1a1a2e' }}>
                  Single Day
                </Typography>
                <Typography sx={{ fontSize: '0.68rem', color: '#999', mt: 0.1 }}>
                  Export for one specific date
                </Typography>
              </Box>
              <Radio checked={exportDateMode === 'single'} size="small" sx={{ p: 0.3, color: '#c5cae9', '&.Mui-checked': { color: '#536DFE' } }} />
            </Box>

            {/* Option 4: All Dates (Full History) */}
            <Box
              onClick={() => setExportDateMode('all')}
              sx={{
                display: 'flex', alignItems: 'center', gap: 1.5,
                px: 1.5, py: 1.2, borderRadius: 2, cursor: 'pointer',
                border: exportDateMode === 'all' ? '1.5px solid #536DFE' : '1.5px solid #e8eaf6',
                bgcolor: exportDateMode === 'all' ? 'rgba(83, 109, 254, 0.04)' : '#fff',
                transition: 'all 0.2s ease',
                '&:hover': { borderColor: '#536DFE', bgcolor: 'rgba(83, 109, 254, 0.02)' },
              }}
            >
              <Box sx={{
                width: 32, height: 32, borderRadius: 1.5,
                bgcolor: exportDateMode === 'all' ? '#536DFE' : '#e8eaf6',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                transition: 'all 0.2s ease', flexShrink: 0,
              }}>
                <CalendarMonth sx={{ fontSize: 16, color: exportDateMode === 'all' ? '#fff' : '#536DFE' }} />
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontWeight: 600, fontSize: '0.8rem', color: '#1a1a2e' }}>
                  All Data (Full History)
                </Typography>
                <Typography sx={{ fontSize: '0.68rem', color: '#999', mt: 0.1 }}>
                  Export full database history without any filters
                </Typography>
              </Box>
              <Radio checked={exportDateMode === 'all'} size="small" sx={{ p: 0.3, color: '#c5cae9', '&.Mui-checked': { color: '#536DFE' } }} />
            </Box>
          </Box>

          {/* Date inputs — compact animated */}
          {exportDateMode === 'custom' && (
            <Box sx={{
              display: 'flex', gap: 1.5, mt: 1.5,
              p: 1.5, borderRadius: 2,
              bgcolor: '#fafbff', border: '1px solid #e8eaf6',
              animation: 'fadeIn 0.2s ease',
              '@keyframes fadeIn': { '0%': { opacity: 0, transform: 'translateY(-4px)' }, '100%': { opacity: 1, transform: 'translateY(0)' } },
            }}>
              <TextField
                label="From Date" type="date"
                value={exportFromDate}
                onChange={(e) => setExportFromDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                fullWidth size="small"
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5, bgcolor: '#fff', fontSize: '0.82rem' }, '& .MuiInputLabel-root': { fontSize: '0.78rem' } }}
              />
              <TextField
                label="To Date" type="date"
                value={exportToDate}
                onChange={(e) => setExportToDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                fullWidth size="small"
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5, bgcolor: '#fff', fontSize: '0.82rem' }, '& .MuiInputLabel-root': { fontSize: '0.78rem' } }}
              />
            </Box>
          )}

          {exportDateMode === 'single' && (
            <Box sx={{
              mt: 1.5, p: 1.5, borderRadius: 2,
              bgcolor: '#fafbff', border: '1px solid #e8eaf6',
              animation: 'fadeIn 0.2s ease',
              '@keyframes fadeIn': { '0%': { opacity: 0, transform: 'translateY(-4px)' }, '100%': { opacity: 1, transform: 'translateY(0)' } },
            }}>
              <TextField
                label="Select Date" type="date"
                value={exportFromDate}
                onChange={(e) => setExportFromDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                fullWidth size="small"
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5, bgcolor: '#fff', fontSize: '0.82rem' }, '& .MuiInputLabel-root': { fontSize: '0.78rem' } }}
              />
            </Box>
          )}
        </DialogContent>

        {/* Actions */}
        <Box sx={{ px: 2.5, py: 1.5, bgcolor: '#fafbff', borderTop: '1px solid #eef2f6', display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
          <Button
            onClick={handleCloseExportDialog}
            variant="outlined" size="small"
            sx={{
              borderRadius: 2, textTransform: 'none', fontWeight: 600,
              px: 2, fontSize: '0.8rem', borderColor: '#c5cae9', color: '#536DFE',
              '&:hover': { borderColor: '#536DFE', bgcolor: 'rgba(83,109,254,0.04)' },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmExport}
            variant="contained" size="small"
            disabled={exportLoading || (exportDateMode === 'custom' && (!exportFromDate || !exportToDate)) || (exportDateMode === 'single' && !exportFromDate)}
            startIcon={exportLoading ? <CircularProgress size={14} color="inherit" /> : <FileDownload sx={{ fontSize: 16 }} />}
            sx={{
              borderRadius: 2, textTransform: 'none', fontWeight: 700,
              px: 2.5, fontSize: '0.8rem',
              background: 'linear-gradient(135deg, #536DFE 0%, #3d5afe 100%)',
              boxShadow: '0 3px 10px rgba(83, 109, 254, 0.3)',
              '&:hover': { background: 'linear-gradient(135deg, #3d5afe 0%, #304ffe 100%)', boxShadow: '0 4px 14px rgba(83, 109, 254, 0.4)' },
              '&.Mui-disabled': { background: '#e0e0e0', boxShadow: 'none' },
            }}
          >
            {exportLoading ? 'Exporting...' : 'Export Now'}
          </Button>
        </Box>
      </Dialog>
      <AlertModal open={alertModal.open} title={alertModal.title} message={alertModal.message} onClose={closeAlert} />
    </Box>
  );
};

export default CancelReport;

